import type { ColumnDef, OnChangeFn, PaginationState, RowSelectionState } from "@tanstack/react-table";
import * as React from "react";
import { useMemo } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { createDataTableSelectColumn } from "@/components/ui/data-table-select-column";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { TRADE_POSITION_SIDE_BADGE_CLASS } from "@/components/trade-position-side-badge";
import { cn } from "@/lib/utils";
import type { Backtest } from "../../../types/backtests";
import { getReportTypeDisplayLabel } from "../../../utils/reportTypeRules";

const ORIGIN_BADGE_STANDALONE_CLASS = cn(
  TRADE_POSITION_SIDE_BADGE_CLASS,
  "normal-case tracking-normal bg-[image:linear-gradient(135deg,#38BBD1,#3EEAEC)]",
);

const ORIGIN_BADGE_REPORT_CLASS = cn(
  TRADE_POSITION_SIDE_BADGE_CLASS,
  "normal-case tracking-normal bg-[image:linear-gradient(135deg,#805AD5,#B794F4)]",
);

function formatCreatedAt(value: string | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type SimulatorListTableProps = {
  rows: Backtest[];
  reportTitlesById: Record<string, string>;
  reportTypesById: Record<string, string>;
  deletingId: string | null;
  isBulkDeleting?: boolean;
  onOpenRun: (id: string) => void;
  onOpenReport: (reportId: string, reportDisplayTitle: string) => void;
  onDelete: (id: string) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  selectAllFromHeader: boolean;
  onSelectAllFromHeaderChange: (viaHeader: boolean) => void;
  toolbarActions?: React.ReactNode;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  /** When false, parent renders pagination below the list card. */
  showPagination?: boolean;
};

export function SimulatorListTable(props: SimulatorListTableProps): React.ReactElement {
  const columns = useMemo((): ColumnDef<Backtest>[] => {
    return [
      createDataTableSelectColumn<Backtest>({
        selectAllFromHeader: props.selectAllFromHeader,
        onSelectAllFromHeaderChange: props.onSelectAllFromHeaderChange,
        headerDataAttributes: {
          "data-simulator-list-select-all-header": "",
        },
      }),
      {
        id: "title",
        accessorFn: (row) => row.title?.trim() || "Untitled simulation",
        header: "Title",
        enableSorting: false,
        cell: ({ row }) => {
          const title = row.original.title?.trim() || "Untitled simulation";
          return (
            <div data-simulator-list-cell="" className="min-w-0 max-w-[360px]">
              <p
                data-simulator-list-title=""
                className="truncate font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
                title={row.original.title || undefined}
              >
                {title}
              </p>
            </div>
          );
        },
      },
      {
        id: "origin",
        accessorFn: (row) => (row.report_id ? "report" : "standalone"),
        header: "Origin",
        cell: ({ row }) => {
          const isReport = Boolean(row.original.report_id);
          return (
            <Badge
              data-simulator-list-origin-badge={isReport ? "report" : "standalone"}
              className={isReport ? ORIGIN_BADGE_REPORT_CLASS : ORIGIN_BADGE_STANDALONE_CLASS}
            >
              {isReport ? "Report" : "Simulator"}
            </Badge>
          );
        },
        enableSorting: false,
      },
      {
        id: "report",
        accessorFn: (row) => {
          if (!row.report_id) return "";
          return props.reportTitlesById[row.report_id] ?? "Unknown report";
        },
        header: "Report",
        cell: ({ row }) => {
          const reportId = row.original.report_id;
          if (!reportId) {
            return (
              <p
                data-simulator-list-muted=""
                className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
              >
                N/A
              </p>
            );
          }
          const reportLabel = props.reportTitlesById[reportId] ?? "Unknown report";
          return (
            <div data-simulator-list-report-cell="" className="flex items-center">
              <Button
                type="button"
                variant="outline-gray"
                size="sm"
                data-font="primary"
                data-simulator-list-report-open=""
                className="h-8 shrink-0 px-3"
                aria-label={`Open report ${reportLabel}`}
                title={reportLabel}
                onClick={(event) => {
                  event.stopPropagation();
                  props.onOpenReport(reportId, reportLabel);
                }}
              >
                <span className="normal-case tracking-normal">Open</span>
              </Button>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "reportType",
        accessorFn: (row) => {
          if (!row.report_id) return "";
          const reportType = props.reportTypesById[row.report_id];
          return reportType ? getReportTypeDisplayLabel(reportType) : "";
        },
        header: "Report type",
        cell: ({ row }) => {
          const reportId = row.original.report_id;
          if (!reportId) {
            return (
              <p
                data-simulator-list-muted=""
                className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
              >
                N/A
              </p>
            );
          }
          const reportType = props.reportTypesById[reportId];
          return (
            <p
              data-simulator-list-muted=""
              className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
            >
              {reportType ? getReportTypeDisplayLabel(reportType) : "—"}
            </p>
          );
        },
        enableSorting: false,
      },
      {
        id: "created",
        accessorFn: (row) => row.created_at ?? "",
        header: ({ column }) => (
          <DataTableColumnHeader<Backtest, unknown> column={column} title="Created" />
        ),
        cell: ({ row }) => (
          <p
            data-simulator-list-muted=""
            data-simulator-list-created=""
            className="whitespace-nowrap font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-white"
          >
            {formatCreatedAt(row.original.created_at)}
          </p>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        meta: { label: "Actions", align: "end" },
        enableResizing: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <TableOutlineDeleteIconButton
              marker="simulator"
              aria-label="Delete simulation"
              disabled={props.deletingId === row.original.id || props.isBulkDeleting}
              onClick={() => props.onDelete(row.original.id)}
            />
          </div>
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ];
  }, [
    props.deletingId,
    props.isBulkDeleting,
    props.onDelete,
    props.onOpenReport,
    props.onSelectAllFromHeaderChange,
    props.reportTitlesById,
    props.reportTypesById,
    props.selectAllFromHeader,
  ]);

  return (
    <div
      data-simulator-list-table=""
      data-trade-log-trades-table=""
      className="flex w-full min-w-0 flex-col gap-4"
    >
      <DataTable
        columns={columns}
        data={props.rows}
        getRowId={(row) => row.id}
        onRowClick={(row) => props.onOpenRun(row.id)}
        enableRowSelection
        rowSelection={props.rowSelection}
        onRowSelectionChange={props.onRowSelectionChange}
        toolbarActions={props.toolbarActions}
        pagination={props.pagination}
        onPaginationChange={props.onPaginationChange}
        showPagination={props.showPagination ?? true}
        paginationAfterShell={props.showPagination ?? true}
        tableClassName="dark:!bg-sidebar"
        emptyMessage="No simulations found."
      />
    </div>
  );
}
